import { Link, useNavigate } from "react-router";
import { DashboardCard } from "../molecules/DashboardCard";
import { DueDateCell } from "../molecules/DueDateCell";
import { DocumentArtifactStatus } from "~/config/types";
import { dateDiff } from "~/helpers/date";
import { useTranslation } from "react-i18next";
import NoResult from "../molecules/NoResult";
import SimpleDataTable, {
  type TableColumn,
} from "../molecules/SimpleDataTable";
import { CenterSpinner } from "../atoms/spinner";
import { Button } from "~/atoms/button";
import { useDocumentReleases } from "~/api/v2/useDocumentReleases";
import {
  hasWorkflowData,
  type DocumentReleaseSummaryWithWorkflow,
} from "~/api/types";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";
import { progressRatio } from "~/helpers/workflow";

const OngoingWorkflowsContainer = () => {
  const { t } = useTranslation();

  const tableColumns: TableColumn<DocumentReleaseSummaryWithWorkflow>[] = [
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) => a.artifact.title.localeCompare(b.artifact.title),
      cell: (v) => (
        <div className="min-w-0">
          <span className="block truncate" title={v.artifact.title}>
            {v.artifact.title}
          </span>
        </div>
      ),
      cellClassName: "w-full max-w-0",
      tableHeadClassName: "w-full",
    },
    {
      header: t(($) => $.commons.workflow.progress),
      compareFn: (a, b) =>
        progressRatio(a.artifact.workflow) - progressRatio(b.artifact.workflow),
      cell: (wf) => <WorkflowProgress workflow={wf.artifact.workflow} />,
      cellClassName: "min-w-32 whitespace-nowrap",
      tableHeadClassName: "min-w-32 whitespace-nowrap",
    },
    {
      header: t(($) => $.commons.document.reviewTo),
      defaultSort: true,
      compareFn: (a, b) => dateDiff(a.artifact.reviewTo, b.artifact.reviewTo),
      cell: (release) => <DueDateCell dueDate={release.artifact.reviewTo} />,
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
  ];
  const navigate = useNavigate();
  const { data: releases, isLoading: isLoadingReleases } = useDocumentReleases({
    statuses: [DocumentArtifactStatus.draft],
    includeWorkflowData: true,
  });

  const activeWorkflows = releases
    ?.filter(hasWorkflowData)
    .filter(
      (release) =>
        release.artifact.workflow.isOngoing &&
        release.artifact.status !== DocumentArtifactStatus.archived &&
        release.artifact.status !== DocumentArtifactStatus.published,
    );

  const containerTitle = t(($) => $.dashboard.ongoing.workflows);

  if (isLoadingReleases) {
    return (
      <DashboardCard title={containerTitle} className="h-80">
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (activeWorkflows?.length === 0) {
    return (
      <DashboardCard title={containerTitle}>
        <NoResult className="h-full" />
      </DashboardCard>
    );
  }

  const seeAllButton = (
    <Button variant="link" asChild>
      <Link to="workflows?tab=ongoing">
        {t(($) => $.commons.buttons.seeAll)}
      </Link>
    </Button>
  );

  return (
    <DashboardCard title={containerTitle} button={seeAllButton}>
      <SimpleDataTable
        columns={tableColumns}
        data={activeWorkflows ?? []}
        onClickRow={(i) => navigate(`/documents/${i.document.id}?tab=workflow`)}
        wrapperClassName="max-h-95"
      />
    </DashboardCard>
  );
};

export default OngoingWorkflowsContainer;
